import Button from 'components/Button';
import CardContent from 'components/CardContent';
import FormActionGroup from 'components/FormActionGroup';
import FormGroup from 'components/FormGroup';
import {
  GooglePlaceAutoComplete,
  TypeGooglePlaceDetail,
} from 'components/GooglePlaceAutoComplete';
import ListWrapper from 'components/ListWrapper';
import SelectField from 'components/SelectField';
import TextField from 'components/TextField';
import {OnboardingContext} from '../../Onboarding';
import {labels} from 'pages/UserForm/UserAddresses/UserAddresses';
import {useContext, useMemo, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {validateEmail} from 'utils/validators';
import Typography from 'components/Typography';
import {get} from 'lodash';
import api from 'api';
import toast from 'react-hot-toast';
import {phoneFormat} from 'helpers/phoneFormat';
import useCompanyConfig from 'hooks/useCompanyConfig';

type FormValue = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birthday: string;
  address: TypeGooglePlaceDetail;
  unit_address: string;
  type: string;
  label: string;
  company: string;
};

function Customer() {
  const {data, setData} = useContext(OnboardingContext);
  const [isLoading, setIsLoading] = useState(false);
  const {isDeliveryManager} = useCompanyConfig();
  const phone = phoneFormat(get(data, 'customer.user.phone', ''));
  const {
    handleSubmit,
    register,
    formState: {errors},
    control,
    setValue,
  } = useForm<FormValue>({
    defaultValues: {
      first_name: get(data, 'customer.user.first_name', ''),
      last_name: get(data, 'customer.user.last_name', ''),
      email: get(data, 'customer.user.email', ''),
      phone,
      address: get(data, 'customer.address', {}),
      unit_address: get(data, 'customer.address.unit_address', ''),
      type: get(data, 'customer.address.type', 'dispatch'),
      label: get(data, 'customer.address.label', 'home'),
    },
  });

  const [location, setLocation] = useState({
    latitude: get(data, 'customer.address.latitude', 40.7596958),
    longitude: get(data, 'customer.address.longitude', -73.9650045),
  });

  const onSubmit = async (values: FormValue) => {
    if (get(data, 'customer.id', 0)) {
      setData({...data, step: 2});
      return;
    }
    try {
      setIsLoading(true);
      const user = {
        email: values.email.trim(),
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        phone: values.phone.replace(/\D/g, ''),

        customer_company: values.company.trim() || '',
      };
      const body = Object.fromEntries(
        Object.entries(user).filter(([_, value]) => Boolean(value))
      );
      const {data: customer} = await api.post('/customers/', body);
      const {data: address} = await api.post<any>(`/customers/address/`, {
        customer_id: Number(customer.id),
        address_line_1: get(values, 'address.addressLine1', ''),
        city: get(values, 'address.city', ''),
        state: get(values, 'address.state', ''),
        unit_address: values.unit_address,
        type: 'dispatch', // address type is dispatch by default
        label: values.label,
        zipcode: get(values, 'address.zipcode', ''),
        latitude: location.latitude,
        longitude: location.longitude,
      });
      setData({
        ...data,
        customer: {
          ...customer,
          address,
        },
        step: 2,
      });
    } catch (error) {
      const message = get(error, 'response.data[0]', 'Something went wrong');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const customerText = useMemo(() => {
    if (isDeliveryManager) {
      return (
        <Typography variant="h4">
          <b>Create your first customer</b> - Customers can place orders and
          request your products.
        </Typography>
      );
    }
    return (
      <Typography variant="h4">
        <b>Create your first customer</b> - Customers can place orders,
        requesting your services or products.
      </Typography>
    );
  }, [isDeliveryManager]);

  return (
    <>
      {customerText}
      <ListWrapper>
        <CardContent px="md" pt="none" pb="md">
          <FormGroup columns={2}>
            <TextField
              label="First Name"
              placeholder="First Name"
              {...register('first_name', {required: 'Required'})}
              error={errors.first_name?.message}
            />
            <TextField
              label="Last Name"
              placeholder="Last Name"
              {...register('last_name', {required: 'Required'})}
              error={errors.last_name?.message}
            />
          </FormGroup>

          <FormGroup columns={2}>
            <TextField
              label="Email"
              placeholder="Email"
              {...register('email', {
                required: 'Required',
                validate: validateEmail,
              })}
              error={errors.email?.message}
            />
            <TextField
              label="Phone"
              placeholder="(123) 456-7890"
              type="tel"
              {...register('phone', {
                required: 'Required',
                minLength: {
                  value: 14,
                  message: 'Enter a valid phone number',
                },
              })}
              onChange={(e) => {
                let input = e.target.value.replace(/\D/g, ''); // Faqat raqamlarni qoldirish

                e.target.value = phoneFormat(input);
              }}
              maxLength={14}
              error={errors.phone?.message}
            />
          </FormGroup>

          <FormGroup columns={2}>
            <TextField
              label="Company"
              placeholder="Company"
              {...register('company')}
              error={errors.company?.message}
            />
            <Controller
              name="unit_address"
              control={control}
              render={({field, fieldState}) => (
                <TextField
                  {...field}
                  label="Unit Number"
                  placeholder="Enter Unit Number"
                  hasError={false}
                  error={fieldState.error?.message}
                />
              )}
            />
          </FormGroup>
          <div style={{display: 'flex', gap: 20}}>
            <div style={{flex: 1}}>
              <FormGroup columns={2}>
                <Controller
                  name="address"
                  control={control}
                  rules={{required: 'Please, Select the Address'}}
                  render={({field, fieldState}) => (
                    <GooglePlaceAutoComplete
                      label="Customer Address"
                      placeholder="Address"
                      onPlaceSelected={(address) => {
                        setLocation({
                          latitude: address.lat,
                          longitude: address.lon,
                        });
                        setValue('address', address);
                      }}
                      defaultValue={get(data, 'customer.address.name', '')}
                      error={fieldState.error?.message}
                      options={{
                        types: ['address'],
                      }}
                    />
                  )}
                />
                <Controller
                  name="label"
                  control={control}
                  rules={{required: 'Please Select Label of Address'}}
                  render={({field: {ref, ...field}, fieldState}) => (
                    <SelectField
                      {...field}
                      label="Address Label"
                      placeholder="Select label"
                      options={labels}
                      error={fieldState.error?.message}
                      value={labels.find((item) => item.value === field.value)}
                      onChange={(newValue) => {
                        if (newValue) {
                          setValue('label', newValue.value as 'home');
                        }
                      }}
                    />
                  )}
                />
              </FormGroup>
            </div>
          </div>
        </CardContent>
      </ListWrapper>
      <FormActionGroup
        style={{justifyContent: 'center', padding: 20, marginTop: 50}}
      >
        <Button onClick={handleSubmit(onSubmit)} loading={isLoading}>
          Next
        </Button>
      </FormActionGroup>
    </>
  );
}

export default Customer;
